import { useQuery, useMutation } from "./urqlHooks";

const CREDITS_EVOLUTION = `query CreditsEvolution($companyId: String!) {
    creditsEvolution(companyId: $companyId) {
      creditId
      docId
      amount
      date
      currency
      bank
      creditType
      originalAmount
      isPaid
      taxAmount
    }
    Credits(companyId: $companyId) {
      id
      credits {
        _id
        bankId
        creditType
        installmentType
        paymentsAmount
        payments
        fees
        feesPeriod
        totalAmount
        emision
        firstPaymentDate
        currency
        paymentsArray
        unpaidStock
        paidPayments
        name
      }
    }
  }`;

export const CreditsEvolution = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: CREDITS_EVOLUTION,
    variables: { companyId },
    pause: !companyId,
    requestPolicy: "network-only",
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const CREDIT_DOCUMENTS = `query GET_CREDIT_DOCUMENTS($companyId: String!){
    allCreditDocumentsByCompany(companyId: $companyId) {
        id
        currencyType
        companyId
        description
        amount
        documentDate
        isHidden
        isManual
        documentType
        meta {
        ... on CreditsMeta
        {
        installments
        interest
        stock
        interestCLP
        stockCLP
        installmentCLP
        isPaid
        taxAmount
        quotaTaxAmount
        commissionAmount
        }
        ... on ObligationMeta
        {
        installments
        interest
        stock
        interestCLP
        stockCLP
        installmentCLP
        isPaid
        taxAmount
        quotaTaxAmount
        commissionAmount
        }
        }               
    }
  }`;

export const CreditDocuments = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: CREDIT_DOCUMENTS,
    variables: { companyId },
    pause: !companyId,
    requestPolicy: "network-only",
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const UPDATE_CREDIT = `mutation ADD_CREDIT($companyId: String!, $credit: CreditInput! ) {
  addCredit(companyId: $companyId, credit: $credit) {
      id
  }
}`;

export const UpdateCredit = () => {
  const [, updateCredit] = useMutation(UPDATE_CREDIT);
  return updateCredit;
};

const GET_PAYMENTS_LIST = `query GET_PAYMENTSARRAYS(
	$companyId: String!
	$IdCredit: String!
){
	PaymentsArray(
		companyId: $companyId, 
		credit: $IdCredit
	){
		name, 
		description,
		id, 
		currencyType,
		companyId
		meta{... on CreditsMeta{isPaid, interest, stock, totalPayments, paymentNumber, taxAmount, quotaTaxAmount, commissionAmount,
    matchs {
      inMatch {
        id
        settleId
        type
        amount
        selected
        userRelated {
          userId
          firstName
          lastName
          date
        }
        accountPlan {
          level1 {
            value
            label
          }
          level2 {
            value
            label
          }
          level3 {
            value
            label
          }
        }
      }
      outMatch {
        id
        amount
        selected
        userRelated {
          userId
          firstName
          lastName
          date
        }
        accountPlan {
          level1 {
            value
            label
          }
          level2 {
            value
            label
          }
          level3 {
            value
            label
          }
        }
      }
    }}
    ... on ObligationMeta{isPaid, interest, stock, totalPayments, paymentNumber, taxAmount, quotaTaxAmount, commissionAmount,
    matchs {
      inMatch {
        id
        settleId
        type
        amount
        selected
        userRelated {
          userId
          firstName
          lastName
          date
        }
        accountPlan {
          level1 {
            value
            label
          }
          level2 {
            value
            label
          }
          level3 {
            value
            label
          }
        }
      }
      outMatch {
        id
        amount
        selected
        userRelated {
          userId
          firstName
          lastName
          date
        }
        accountPlan {
          level1 {
            value
            label
          }
          level2 {
            value
            label
          }
          level3 {
            value
            label
          }
        }
      }
    }}}
		amount
		documentDate
		isHidden
		isManual
		documentType
		pointsWereUpdated
		hideDocument
		company{name}
	}
}`;

export const GetPaymentList = (companyId, IdCredit) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_PAYMENTS_LIST,
    variables: { companyId, IdCredit },
    pause: !IdCredit || !companyId,
    requestPolicy: "network-only",
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const UPDATE_CREDIT_ROW = `mutation UPDATE_CREDIT_TABLE(
  $companyId: String!
  $credit: String!
  $payment: String!
  $input: PaymentUpdateInput!
){
  updatePayment(
      companyId: $companyId,
      credit: $credit,
      payment: $payment,
      input: $input
  ){
      id
  }
}`;

export const UpdateCreditRow = () => {
  const [, updateCreditRow] = useMutation(UPDATE_CREDIT_ROW);
  return updateCreditRow;
};

const GENERATE_VARIABLE_SKELETON = `mutation GENERATE_VARIABLE_SKELETON(
  $companyId: String!
  $credit: String!
  $paymentsCount: Int!
) {
  generateVariableCreditSkeleton(
    companyId: $companyId
    credit: $credit
    paymentsCount: $paymentsCount
  ) {
    id
    documentDate
    amount
    meta {
      ... on CreditsMeta {
        paymentNumber
        stock
        interest
        quotaTaxAmount
        commissionAmount
        isPaid
      }
      ... on ObligationMeta {
        paymentNumber
        stock
        interest
        quotaTaxAmount
        commissionAmount
        isPaid
      }
    }
  }
}`;

export const GenerateVariableCreditSkeleton = () => {
  const [, generateVariableCreditSkeleton] = useMutation(
    GENERATE_VARIABLE_SKELETON,
  );
  return generateVariableCreditSkeleton;
};


const DELETE_CREDIT = `mutation DELETE_CREDIT(
  $companyId: String!,
  $credit: String!
){
  deleteCredit(
    companyId: $companyId,
    credit: $credit
  ){
    id
  }
}`;

export const DeleteCredit = () => {
  const [result, deleteCredit] = useMutation(DELETE_CREDIT);
  const { data, fetching, error } = result;
  return { data, fetching, error, deleteCredit };
}

